/**
 * Prefer server JSON body over Axios' generic "Request failed with status code N".
 */

function asErrorRecord(data: unknown): Record<string, unknown> | null {
  if (data == null) return null;
  if (typeof data === "string") {
    const t = data.trim();
    if (!t) return null;
    try {
      const p = JSON.parse(t) as unknown;
      if (p && typeof p === "object" && !Array.isArray(p)) {
        return p as Record<string, unknown>;
      }
    } catch {
      return { message: t };
    }
    return null;
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

function pickStr(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v == null) return "";
  return String(v).trim();
}

export function getApiErrorCode(error: unknown): string | undefined {
  const e = error as { code?: string; response?: { data?: unknown } };
  if (typeof e?.code === "string" && e.code.trim()) {
    return e.code.trim();
  }
  const rec = asErrorRecord(e?.response?.data);
  const code = pickStr(rec?.code);
  return code || undefined;
}

export function isThreadBusyError(error: unknown): boolean {
  return getApiErrorCode(error) === "thread_busy";
}

const THREAD_BUSY_USER_MESSAGE =
  "Theo is still working on the previous message. Please wait a moment before trying again.";

/** User-facing copy for chat send / HITL failures (never throws). */
export function getChatSendErrorMessage(
  error: unknown,
  fallback = "Failed to send message. Please try again."
): string {
  if (isThreadBusyError(error)) {
    return THREAD_BUSY_USER_MESSAGE;
  }
  return getApiErrorMessage(error, fallback);
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong."
): string {
  const e = error as {
    response?: { data?: unknown };
    message?: string;
  };
  const rec = asErrorRecord(e?.response?.data);
  if (rec) {
    const details = pickStr(rec.details) || pickStr(rec.detail);
    if (details) return details;
    const nested = rec.error;
    if (typeof nested === "string") {
      const errField = pickStr(nested);
      if (errField) return errField;
    }
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const nestedRecord = nested as Record<string, unknown>;
      const nestedDetails =
        pickStr(nestedRecord.details) || pickStr(nestedRecord.detail);
      if (nestedDetails) return nestedDetails;
      const nestedMessage = pickStr(nestedRecord.message);
      if (nestedMessage) return nestedMessage;
      const nestedError = pickStr(nestedRecord.error);
      if (nestedError) return nestedError;
    }
    const msg = pickStr(rec.message);
    if (msg) return msg;
  }
  const m = e?.message;
  if (
    typeof m === "string" &&
    m.trim() &&
    !/^Request failed with status code \d+$/i.test(m.trim())
  ) {
    return m.trim();
  }
  return fallback;
}
